import { Pencil1Icon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { ITenant } from "../../../@types/admin/AdminHospital";
import { TableSorted } from "../../../components/TableSorted";
import { useTenants } from "../../../hooks/queries/admin/hospitals";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FilterButton,
  FiltersActions,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  SearchInput,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { TenantModal } from "./TenantModal";
import { TenantRegionBadge } from "./TenantRegionBadge";

const SEARCH_FIELDS: (keyof ITenant)[] = ["id", "name", "region"];

export function AdminTenantsList() {
  document.title = "Tenants - Admin";

  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { tenants, isLoading, isError } = useTenants();

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: tenants, searchFields: SEARCH_FIELDS });

  const openTenantModal = (tenant?: ITenant) => {
    openModal(<TenantModal tenant={tenant} onClose={closeModal} />, {
      width: "440px",
      showCloseButton: true,
    });
  };

  const columns: ColumnDef<ITenant, string>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => <span>{row.original.name}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "region",
        header: "Region",
        cell: ({ row }) => <TenantRegionBadge region={row.original.region} />,
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => <span>{row.original.id}</span>,
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowAction
            label="Edit"
            icon={<Pencil1Icon />}
            onClick={() => {
              openTenantModal(row.original);
            }}
          />
        ),
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Tenants</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by name, region, or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FiltersActions>
          <FilterButton variant="primary" onClick={() => openTenantModal()}>
            Add tenant
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the tenants. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-tenants"]]}>
          <TableSorted
            columns={columns}
            data={items}
            onRowClick={(tenant: ITenant) =>
              navigate(`/admin/tenants/${tenant.id}`)
            }
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "tenants",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
